/**
 * @fileoverview Legal mentions page component.
 * @summary Displays legal information about KaraoNow.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/LegalPage
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { useTranslation } from 'react-i18next'
import PageHeader from '../components/PageHeader'

/**
 * @summary Legal mentions page component.
 * @returns {React.ReactElement} Legal mentions page.
 */
export default function LegalPage() {
  const { t } = useTranslation()

  return (
    <div className="min-h-screen bg-base-50 dark:bg-base-900">
      <PageHeader
        title={t('legalPage.title')}
        subtitle={t('legalPage.subtitle')}
      />

      <div className="page-container mx-auto py-4 px-4 bg-white dark:bg-gray-800 rounded-lg">
        <div className="prose dark:prose-invert max-w-none">
          {/* Section 1: Editor */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('legalPage.editor.title')}
            </h2>
            <div className="space-y-2 text-(--color-text-secondary)">
              <p>
                <strong>{t('legalPage.editor.name')}</strong>
              </p>
              <p>{t('legalPage.editor.address')}</p>
              {/* <p>{t('legalPage.editor.phone')}</p> */}
              <p>{t('legalPage.editor.email')}</p>
              <p>{t('legalPage.editor.siret')}</p>
            </div>
          </section>

          {/* Section 2: Publication Director */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('legalPage.publication.title')}
            </h2>
            <p className="text-(--color-text-secondary)">
              {t('legalPage.publication.director')}
            </p>
          </section>

          {/* Section 3: Hosting */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('legalPage.hosting.title')}
            </h2>
            <div className="space-y-2 text-(--color-text-secondary)">
              <p>
                <strong>Render.com</strong>
              </p>
              <p>625 2nd Street, Suite 100</p>
              <p>San Francisco, CA 94107</p>
              <p>United States</p>
              <p>
                <a
                  href="https://render.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline"
                >
                  https://render.com
                </a>
              </p>
            </div>
          </section>

          {/* Section 4: Data Protection */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('legalPage.dataProtection.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('legalPage.dataProtection.description')}
            </p>
            <p className="text-(--color-text-secondary)">
              <a
                href="/privacy"
                className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline"
              >
                {t('legalPage.dataProtection.link')}
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}

